import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MagnifyingGlass, Coin, Stethoscope } from '@phosphor-icons/react';

const PublicServicesPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [q, setQ] = useState('');
  const [profFilter, setProfFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get('/api/services-catalog'),
          api.get('/api/professions'),
        ]);
        setServices(s.data);
        setProfessions(p.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const professionLabel = useMemo(() => {
    const map = {};
    professions.forEach((p) => { map[p.code] = p.label; });
    return map;
  }, [professions]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return services.filter((s) => {
      if (!s.active) return false;
      if (profFilter !== 'all' && !(s.profession_codes || []).includes(profFilter)) return false;
      if (query && !s.name.toLowerCase().includes(query) && !(s.description || '').toLowerCase().includes(query)) return false;
      return true;
    });
  }, [services, q, profFilter]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-12" data-testid="public-services-page">
        {/* Hero */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Stethoscope size={14} weight="duotone" /> Catalogo prestazioni VivaLink Italia
          </div>
          <h1 className="font-heading font-semibold text-4xl sm:text-5xl mb-3">Tutte le Prestazioni</h1>
          <p className="text-foreground/70 max-w-2xl mx-auto">
            Esplora tutti i servizi sanitari e assistenziali disponibili sulla piattaforma.
            Ogni prestazione è erogata da professionisti qualificati o Centri Assistenza Viva.
          </p>
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-3 gap-3 mb-8">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass className="absolute left-3 top-3 text-foreground/50" size={18} />
            <Input
              placeholder="Cerca una prestazione…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10"
              data-testid="services-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap md:justify-end">
            <button
              onClick={() => setProfFilter('all')}
              className={`px-3 py-1 rounded-full text-xs border ${profFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
              data-testid="filter-all"
            >
              Tutte
            </button>
            {professions.slice(0, 8).map((p) => (
              <button
                key={p.code}
                onClick={() => setProfFilter(p.code)}
                className={`px-3 py-1 rounded-full text-xs border ${profFilter === p.code ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border'}`}
                data-testid={`filter-${p.code}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="text-center text-foreground/60 py-10">Caricamento…</p>
        ) : filtered.length === 0 ? (
          <Card className="neumorphism rounded-2xl p-10 text-center text-foreground/60">
            Nessuna prestazione trovata per i filtri selezionati.
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s) => (
              <Card key={s.service_id} className="neumorphism rounded-2xl p-6 flex flex-col" data-testid={`svc-card-${s.service_id}`}>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">{s.name}</h3>
                  {s.description && (
                    <p className="text-sm text-foreground/70 mb-3 line-clamp-2">{s.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(s.profession_codes || []).map((c) => (
                      <Badge key={c} variant="outline" className="text-xs">
                        {professionLabel[c] || c}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-border/60 pt-3 mt-2">
                  <div>
                    <div className="text-xs text-foreground/60">Prezzo indicativo</div>
                    <div className="text-lg font-semibold">€{Number(s.default_price_eur || 0).toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-foreground/60">
                    <Coin size={14} weight="duotone" />
                    {s.credit_cost} crediti pro
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          <Card className="neumorphism rounded-2xl p-8 text-center">
            <h3 className="font-heading font-semibold text-2xl mb-2">Hai bisogno di assistenza?</h3>
            <p className="text-foreground/70 mb-4">Crea una richiesta e riceverai proposte dai professionisti della tua zona.</p>
            <Button className="rounded-full" onClick={() => navigate('/request')} data-testid="cta-request-btn">
              Richiedi assistenza
            </Button>
          </Card>
          <Card className="neumorphism rounded-2xl p-8 text-center">
            <h3 className="font-heading font-semibold text-2xl mb-2">Sei un professionista?</h3>
            <p className="text-foreground/70 mb-4">Registrati in 2 semplici passaggi e inizia a ricevere le richieste della tua zona.</p>
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/register-professional')} data-testid="cta-register-pro-btn">
              Diventa professionista
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PublicServicesPage;
